<template>
  <Modal ref="modal" title="Cadastrar veículos no Detran" :force="loading">
    <div class="associar-container">
      <div class="regras">
        <InformationOutline :size="20" class="regras-icon" />

        <ul>
          <li>A categoria de cada veículo é definida pelo Detran a partir da placa.</li>
          <li>Apenas 1 veículo principal por categoria e até 3 veículos não principais.</li>
          <li>Veículo não principal exige o período de utilização.</li>
          <li>Se o instrutor já tem um veículo principal na categoria, cadastre o novo como não principal.</li>
          <li>Se algum veículo for recusado, nenhum é cadastrado.</li>
        </ul>
      </div>

      <form @submit.prevent="handleSubmit">
        <Select v-if="selecionarAutonomo" :filtered="true" :options="autonomos" legend="Instrutor autônomo"
          placeholder="Selecione o instrutor autônomo" :modelValue="autonomo"
          @update:modelValue="$event => (autonomo = $event)" :disabled="loading" />

        <div class="veiculos">
          <div v-for="(veiculo, index) in veiculos" :key="veiculo.key" class="veiculo">
            <div class="veiculo-header">
              <span class="veiculo-titulo">Veículo {{ index + 1 }}</span>

              <IconButton v-if="veiculos.length > 1" type="button" color="danger" title="Remover veículo"
                :disabled="loading" @handle:click="removerVeiculo(index)">
                <DeleteOutline :size="19" />
              </IconButton>
            </div>

            <div class="veiculo-campos">
              <Input class="campo-placa" legend="Placa" placeholder="ABC1D23" :modelValue="veiculo.placa"
                @update:modelValue="$event => (veiculo.placa = formatarPlaca($event))" :disabled="loading" />

              <!-- Botões em vez de select: o menu do select seria cortado pelo scroll da lista de veículos -->
              <div class="campo-tipo">
                <legend>Tipo do veículo</legend>

                <div class="tipo-opcoes" role="radiogroup" aria-label="Tipo do veículo">
                  <button v-for="opcao in tiposVeiculo" :key="opcao.value" type="button" role="radio"
                    class="tipo-opcao" :class="{ selecionado: veiculo.tipo === opcao.value }"
                    :aria-checked="veiculo.tipo === opcao.value" :disabled="loading"
                    @click="veiculo.tipo = opcao.value">
                    {{ opcao.text }}
                  </button>
                </div>
              </div>

              <template v-if="veiculo.tipo === TIPO_NAO_PRINCIPAL">
                <SingleDate class="campo-data" legend="Início da utilização" :modelValue="veiculo.inicio"
                  @update:modelValue="$event => (veiculo.inicio = $event)" :disabled="loading" />

                <SingleDate class="campo-data" legend="Fim da utilização" :modelValue="veiculo.fim"
                  @update:modelValue="$event => (veiculo.fim = $event)" :minDate="veiculo.inicio || null"
                  :disabled="loading" />
              </template>
            </div>
          </div>
        </div>

        <Button class="adicionar" type="button" text="Adicionar veículo" color="primary" :fill="false"
          :disabled="loading || veiculos.length >= limiteVeiculos" @handle:click="adicionarVeiculo">
          <Plus :size="18" />
        </Button>

        <div class="actions">
          <Button type="button" text="Cancelar" color="danger" :fill="false" :disabled="loading"
            @handle:click="close" />
          <Button type="submit" :text="loading ? 'Enviando ao Detran...' : 'Cadastrar no Detran'" color="primary"
            :disabled="loading" />
        </div>
      </form>
    </div>
  </Modal>
</template>

<script>
import Modal from 'core/Modal'
import Button from 'core/Button'
import IconButton from 'core/IconButton'
import Input from 'core/Input'
import Select from 'core/Select'
import SingleDate from 'core/SingleDate'
import urlApiV2 from '../../router/urlApiV2'
import InformationOutline from 'icons/InformationOutline'
import DeleteOutline from 'icons/DeleteOutline'
import Plus from 'icons/Plus'

const PLACA_PADRAO = /^[A-Z]{3}\d{4}$/
const PLACA_MERCOSUL = /^[A-Z]{3}\d[A-Z]\d{2}$/

const TIPO_PRINCIPAL = 'principal'
const TIPO_NAO_PRINCIPAL = 'nao_principal'

let proximaKey = 0

// Sem tipo padrão: enviar "principal" por engano é recusado pelo Detran quando já existe um (015)
const novoVeiculo = () => ({
  key: proximaKey++,
  placa: '',
  tipo: '',
  inicio: '',
  fim: '',
})

export default {
  name: 'AssociarVeiculosModal',
  props: {
    // Monitoramento/suporte: escolhe o autônomo; os demais usam o schema do próprio login
    selecionarAutonomo: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    Modal,
    Button,
    IconButton,
    Input,
    Select,
    SingleDate,
    InformationOutline,
    DeleteOutline,
    Plus,
  },
  data () {
    return {
      veiculos: [novoVeiculo()],
      // Principais (1 por categoria, até 5 categorias) + 3 não principais
      limiteVeiculos: 8,
      loading: false,
      autonomo: '',
      autonomos: [],
      TIPO_NAO_PRINCIPAL,
      tiposVeiculo: [
        { value: TIPO_PRINCIPAL, text: 'Principal' },
        { value: TIPO_NAO_PRINCIPAL, text: 'Não principal' },
      ],
    }
  },
  methods: {
    open (autonomo = '') {
      this.veiculos = [novoVeiculo()]
      this.autonomo = autonomo
      this.$refs.modal.open()

      if (this.selecionarAutonomo && this.autonomos.length === 0) {
        this.fetchAutonomos()
      }
    },
    async fetchAutonomos () {
      await this.$http.get(`${urlApiV2}/autoescola/todas`)
        .then(response => {
          this.autonomos = (response.body.dados || [])
            .filter(cfc => cfc.tenant_schema && cfc.tenant_schema.startsWith('autonomo_'))
            .map(cfc => ({ value: cfc.tenant_schema, text: cfc.nome_fantasia }))
        })
        .catch(() => {
          this.toast('Erro ao buscar os instrutores autônomos.')
        })
    },
    close () {
      this.$refs.modal.close()
    },
    adicionarVeiculo () {
      this.veiculos.push(novoVeiculo())
    },
    removerVeiculo (index) {
      this.veiculos.splice(index, 1)
    },
    formatarPlaca (valor) {
      return valor.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 7)
    },
    validar () {
      if (this.selecionarAutonomo && !this.autonomo) {
        return 'Selecione o instrutor autônomo.'
      }

      const placas = []

      for (const [index, veiculo] of this.veiculos.entries()) {
        const nome = `Veículo ${index + 1}`

        if (!PLACA_PADRAO.test(veiculo.placa) && !PLACA_MERCOSUL.test(veiculo.placa)) {
          return `${nome}: placa inválida. Use o padrão ABC1234 ou ABC1D23.`
        }

        if (placas.includes(veiculo.placa)) {
          return `${nome}: a placa ${veiculo.placa} foi informada mais de uma vez.`
        }
        placas.push(veiculo.placa)

        if (!veiculo.tipo) {
          return `${nome}: informe se o veículo é principal ou não principal.`
        }

        if (veiculo.tipo === TIPO_NAO_PRINCIPAL) {
          if (!veiculo.inicio || !veiculo.fim) {
            return `${nome}: informe o início e o fim da utilização.`
          }

          if (veiculo.fim < veiculo.inicio) {
            return `${nome}: o fim da utilização deve ser igual ou posterior ao início.`
          }
        }
      }

      if (this.veiculos.filter(veiculo => veiculo.tipo === TIPO_NAO_PRINCIPAL).length > 3) {
        return 'O Detran permite no máximo 3 veículos não principais.'
      }

      return null
    },
    async handleSubmit () {
      const erro = this.validar()

      if (erro) {
        this.toast(erro)
        return
      }

      this.loading = true

      const veiculos = this.veiculos.map(veiculo => {
        const principal = veiculo.tipo === TIPO_PRINCIPAL
        const item = { placa: veiculo.placa, principal }

        if (!principal) {
          item.data_inicio_utilizacao = veiculo.inicio
          item.data_fim_utilizacao = veiculo.fim
        }

        return item
      })

      const payload = this.selecionarAutonomo ? { veiculos, schema: this.autonomo } : { veiculos }

      await this.$http.post(`${urlApiV2}/getran/autonomo/veiculos`, payload)
        .then(response => {
          const dados = response.body.dados || {}
          const cadastrados = (dados.veiculos || [])
            .map(veiculo => `${veiculo.placa} (cat. ${veiculo.categoria}, ${veiculo.principal ? 'principal' : 'não principal'})`)
            .join(', ')

          this.toast(`Veículos cadastrados no Detran: ${cadastrados}`, 'success')

          // Aceito pelo Detran, mas não salvo localmente: avisar para não tentar cadastrar de novo
          if (dados.falhas_locais && dados.falhas_locais.length) {
            this.toast(response.body.mensagem)
          }

          this.close()
          this.$emit('veiculoCadastrado')
        })
        .catch(err => {
          if (err.body && err.body.errors) {
            Object.values(err.body.errors).flat().forEach(mensagem => this.toast(mensagem))
          } else if (err.body && err.body.mensagem) {
            this.toast(`${err.body.mensagem}. Nenhum veículo foi cadastrado.`)
          } else {
            this.toast('Erro ao cadastrar veículos no Detran.')
          }
        })
        .finally(() => {
          this.loading = false
        })
    },
    toast (text, type = 'error') {
      this.$toasted.show(text, {
        icon: type === 'success' ? 'fa-check-circle' : 'fa-times',
        position: 'top-right',
        duration: 6000,
        fullWidth: false,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.associar-container {
  // O Modal core não limita a largura: desconta o padding dele (2rem de cada lado) e uma margem
  width: 44rem;
  max-width: calc(100vw - 5.5rem);
  display: flex;
  flex-direction: column;
  gap: 1.5rem;

  .regras {
    display: flex;
    gap: 0.75rem;
    padding: 1rem;
    border-radius: 0.5rem;
    background-color: rgba(28, 45, 90, 0.05);
    color: #1c2d5a;

    .regras-icon {
      flex-shrink: 0;
    }

    ul {
      margin: 0;
      padding-left: 1rem;
      font-size: 0.8125rem;
      line-height: 1.4;
    }
  }

  form {
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
  }

  .veiculos {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    max-height: 50vh;
    overflow-y: auto;
  }

  .veiculo {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    padding: 1rem;
    border: 1px solid #ececec;
    border-radius: 0.5rem;

    .veiculo-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      min-height: 1.75rem;

      .veiculo-titulo {
        font-size: 0.875rem;
        font-weight: 700;
        color: #1c2d5a;
      }
    }

    .veiculo-campos {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      align-items: end;
      gap: 1rem;
    }
  }

  // Placas são sempre em maiúsculas; o valor enviado também é convertido em formatarPlaca
  .campo-placa ::v-deep(input) {
    text-transform: uppercase;
  }

  .campo-tipo {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    legend {
      margin: 0;
      font-size: 1rem;
      font-weight: 600;
      color: #606264;
      line-height: 21.82px;
    }
  }

  .tipo-opcoes {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.5rem;
  }

  .tipo-opcao {
    min-height: 2.75rem;
    padding: 0 0.75rem;
    border: 1px solid #dfdcdc;
    border-radius: 4px;
    background-color: #ffffff;
    color: #606264;
    font-size: 0.875rem;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.2s, border-color 0.2s, color 0.2s;

    &:hover:not(:disabled) {
      border-color: #1c2d5a;
    }

    &:focus-visible {
      outline: 2px solid #1c2d5a;
      outline-offset: 2px;
    }

    &.selecionado {
      border-color: #1c2d5a;
      background-color: #1c2d5a;
      color: #ffffff;
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.6;
    }
  }

  .adicionar {
    align-self: flex-start;
  }

  .actions {
    align-self: flex-end;
    display: flex;
    gap: 1rem;
  }

  @media (max-width: 576px) {
    .veiculo .veiculo-campos {
      grid-template-columns: minmax(0, 1fr);
    }

    .adicionar,
    .actions {
      align-self: stretch;
    }

    .actions {
      flex-direction: column-reverse;
    }
  }
}
</style>
